import { generatePath } from "react-router-dom";
import { createSSCampaign } from "graphQL/mutations/campaign";
import { EDDM_ROUTING } from "config/routing";
import { CAMPAIGN_ENUM_TYPES, CAMPAIGN_SUBTYPES_ENUM, DMDocumentFormats } from "shared/constants";

// AC34 — "Switch to EDDM" starts a *fresh* EDDM campaign; nothing carries over
// from the current Flyering/D2D campaign. Mirrors the legacy min-budget modal
// switch: create a brand-new EDDM self-serve campaign and navigate the user to
// its (legacy) builder. The new 3-step builder does not yet serve EDDM, so the
// destination is the existing EDDM flow.
const MAX_RETRIES = 10;

interface SwitchToEddmParams {
  clientId: string | number;
  push: (path: string) => void;
}

// Rejects on failure so the caller can show a visible error + re-enable its
// trigger; navigates and resolves on success. Only a duplicate-name collision
// is retried (with a numeric suffix) — any other error (network, 401, 500)
// surfaces immediately rather than being silently retried.
export const switchToEddm = async ({ clientId, push }: SwitchToEddmParams): Promise<void> => {
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const name = attempt === 1 ? "EDDM Campaign" : `EDDM Campaign ${attempt}`;
    const response = await createSSCampaign({
      name,
      type: CAMPAIGN_ENUM_TYPES.DIRECTMAIL,
      subtype: CAMPAIGN_SUBTYPES_ENUM.EDDM,
      flyerType: DMDocumentFormats.BEST_SELLER,
      flyerWeight: null,
      baPerLocation: null,
      lastActiveStep: "home",
    });
    const result = await (response as Response).json();
    if (result.errors) {
      const isDuplicate = result.errors.some((e: { message?: string }) =>
        e.message?.includes("IDX_UNIQUE_CAMPAIGN_CLIENT")
      );
      if (isDuplicate && attempt < MAX_RETRIES) continue;
      throw new Error(result.errors[0]?.message || "Failed to create EDDM campaign");
    }
    const {
      data: {
        createSSCampaign: { campaignId: newCampaignId },
      },
    } = result;
    push(
      generatePath(EDDM_ROUTING.EDDM_CAMPAIGN_SEGMENTS, {
        campaignId: newCampaignId,
        clientId: String(clientId),
      })
    );
    return;
  }
};
